import {
  CAMPAIGN_MARKETPLACE_CATEGORY_OPTIONS,
  INFLUENCER_MARKETPLACE_CATEGORY_OPTIONS,
} from '../../constants/marketplaceSearchFilters';
import { getCategoryRailDisplayLabel } from '../../constants/marketplaceCategories';
import { normalizeMarketplaceKeyword } from './marketplaceKeywordSearch';

export const getMarketplaceSuggestionLabel = (category) => (
  getCategoryRailDisplayLabel(category) || category
);

const MARKETPLACE_TYPED_SUGGESTION_LIMIT = 8;

const CREATOR_POPULAR_PHRASES = Object.freeze([
  'skincare routine',
  'TikTok beauty',
  'fitness creators',
  'luxury fashion',
  'gaming livestream',
  'travel vlog',
  'wellness tips',
  'streetwear style',
]);

const CAMPAIGN_POPULAR_PHRASES = Object.freeze([
  'beauty campaign',
  'skincare launch',
  'fashion collab',
  'product seeding',
  'TikTok shop',
  'home lifestyle',
  'pet supplies',
  'food & beverage',
]);

export const getMarketplaceCategoryOptionsForSuggestions = (userType) => (
  userType === 'influencer'
    ? CAMPAIGN_MARKETPLACE_CATEGORY_OPTIONS
    : INFLUENCER_MARKETPLACE_CATEGORY_OPTIONS
);

const getMarketplacePopularPhrases = (userType) => (
  userType === 'influencer' ? CAMPAIGN_POPULAR_PHRASES : CREATOR_POPULAR_PHRASES
);

const includesIgnoreCase = (haystack, needle) => (
  String(haystack || '').toLowerCase().includes(String(needle || '').toLowerCase())
);

/**
 * Split label into highlight tokens.
 * Tokens with match:true are the typed query portions (muted);
 * match:false are suggested additions (bold).
 */
export const highlightMarketplaceSuggestion = (label, query) => {
  const text = String(label || '');
  const needle = String(query || '').trim();
  if (!text) return [];
  if (!needle) return [{ id: 't-0', text, match: false }];

  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const tokens = [];
  let cursor = 0;

  while (cursor < text.length) {
    const hit = lowerText.indexOf(lowerNeedle, cursor);
    if (hit === -1) {
      tokens.push({
        id: `t-${cursor}`,
        text: text.slice(cursor),
        match: false,
      });
      break;
    }
    if (hit > cursor) {
      tokens.push({
        id: `t-${cursor}`,
        text: text.slice(cursor, hit),
        match: false,
      });
    }
    tokens.push({
      id: `t-${hit}`,
      text: text.slice(hit, hit + needle.length),
      match: true,
    });
    cursor = hit + needle.length;
  }

  return tokens.filter((token) => token.text.length > 0);
};

/**
 * Typed autocomplete rows: matching phrases/categories, nested scopes,
 * and a trailing "Search for {q}" row.
 */
export const getTypedMarketplaceSuggestions = (query, userType) => {
  const normalized = normalizeMarketplaceKeyword(query);
  if (!normalized) return [];

  const categories = getMarketplaceCategoryOptionsForSuggestions(userType);
  const phrases = getMarketplacePopularPhrases(userType);
  const rows = [];
  const seen = new Set();
  const matchLimit = Math.max(MARKETPLACE_TYPED_SUGGESTION_LIMIT - 1, 1);

  const pushRow = (row) => {
    if (seen.has(row.id) || rows.length >= matchLimit) return;
    seen.add(row.id);
    rows.push(row);
  };

  categories.forEach((category) => {
    if (!includesIgnoreCase(category, normalized)) return;
    const label = getMarketplaceSuggestionLabel(category);
    pushRow({
      id: `category-${category}`,
      kind: 'category',
      label,
      category,
      keyword: category,
    });
    pushRow({
      id: `scope-${category}`,
      kind: 'category-scope',
      label,
      category,
      keyword: normalized,
    });
  });

  phrases.forEach((phrase) => {
    if (!includesIgnoreCase(phrase, normalized)) return;
    pushRow({
      id: `phrase-${phrase}`,
      kind: 'phrase',
      label: phrase,
      keyword: phrase,
    });
  });

  const withoutExactQueryDup = rows.filter((row) => {
    if (row.kind !== 'phrase') return true;
    return normalizeMarketplaceKeyword(row.keyword) !== normalized
      || normalizeMarketplaceKeyword(row.label) !== normalized;
  });

  withoutExactQueryDup.push({
    id: `query-${normalized}`,
    kind: 'phrase',
    label: `Search for “${normalized}”`,
    keyword: normalized,
  });

  return withoutExactQueryDup.slice(0, MARKETPLACE_TYPED_SUGGESTION_LIMIT);
};
